/**
 * The papers card on a work order.
 *
 * Two things in one card, deliberately, because they are one file to the person
 * looking at it:
 *
 *   1. THE SIGNED COPY. Once the contractor has acknowledged the order, the
 *      Procurement Manager scans the paper the contractor actually signed and
 *      puts it here. Until acknowledgement the card says so rather than
 *      offering the option - there is no signed copy of an order nobody has
 *      sent, and a control that can be used before it means anything is a
 *      control that stops meaning anything.
 *
 *   2. EVERYTHING ELSE. The quotation the rates came from, PAN, GST and bank
 *      details, MSME and Udyam, labour licence, insurance, test and experience
 *      certificates. These may go on at any time, including while the order is
 *      still a draft, because they are what Procurement collected BEFORE
 *      deciding to place it.
 *
 * WHO SEES THIS CARD
 * ------------------
 * Accounts, Procurement Managers, the Project Manager, the CMD, the CXO, the
 * Head of Procurement and the Project Head - the list the board gave on
 * 14-09-2026. Everybody else gets nothing drawn at all, rather than a card
 * saying "no documents", which would be a statement about the contractor when
 * it is really a statement about the reader.
 *
 * The list here decides whether to DRAW it. fn_may_see_wo_docs() in migration
 * 0124 decides whether the files come back, and it also checks the site, so a
 * Project Manager sees the papers of the contracts he is responsible for and
 * nobody else's. This file can be wrong and no file leaks; the database cannot.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  Download, ExternalLink, FileCheck2, Paperclip, ShieldCheck, Upload,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dateTime } from '@/lib/format'
import { Card, Loading, Select, useToast } from '@/components/ui'
import { ATTACHMENT_KIND_LABEL, saveAttachment } from '@/components/docs/shared'
import {
  listWoPapers, uploadWoPaper, woPaperViewUrl, WO_PAPER_BUCKET, type WoPaper,
} from '../lib/papers'

/** Mirrors fn_may_see_wo_docs(). The database is the authority. */
export const MAY_SEE_WO_PAPERS = [
  'admin', 'procurement_manager', 'procurement_head', 'project_head',
  'project_manager', 'cmd', 'cxo', 'accounts_spoc',
] as const

/** Mirrors fn_may_edit_wo_docs(). Exactly the roles that may raise an order. */
export const MAY_EDIT_WO_PAPERS = [
  'admin', 'procurement_manager', 'procurement_head', 'project_head',
] as const

export const maySeeWoPapers = (role?: string | null) =>
  (MAY_SEE_WO_PAPERS as readonly string[]).includes(role ?? '')
export const mayEditWoPapers = (role?: string | null) =>
  (MAY_EDIT_WO_PAPERS as readonly string[]).includes(role ?? '')

/** What may be attached, in the order Procurement reaches for them. */
const KINDS = [
  'QUOTATION', 'PAN', 'GST_CERT', 'BANK', 'MSME', 'UDYAM',
  'LABOUR_LICENCE', 'INSURANCE', 'TEST_CERT', 'EXPERIENCE', 'DRAWING',
  'APPROVAL', 'OTHER',
]

export function WoPapers({ woId, status }: { woId: string; status: string }) {
  const { profile } = useAuth()
  const toast = useToast()
  const [papers, setPapers] = useState<WoPaper[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [kind, setKind] = useState(KINDS[0])

  const load = useCallback(async () => {
    setLoading(true)
    setPapers(await listWoPapers(woId))
    setLoading(false)
  }, [woId])

  useEffect(() => { void load() }, [load])

  if (!maySeeWoPapers(profile?.role)) return null

  const canEdit = mayEditWoPapers(profile?.role)
  // VENDACK, and anything after it, means the contractor has put his name to
  // this order. CLOSED and CANCELLED are included because an order can be
  // signed and then closed, and the scan is still the record of what he agreed.
  const acknowledged = ['VENDACK', 'CLOSED', 'CANCELLED'].includes(status)
  const signed = papers.filter((p) => p.kind === 'SIGNED_WO')
  const others = papers.filter((p) => p.kind !== 'SIGNED_WO')

  async function put(k: string, file: File) {
    setBusy('upload')
    try {
      await uploadWoPaper(woId, k, file)
      toast.success(`${file.name} attached.`)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'That file could not be attached')
    } finally { setBusy(null) }
  }

  async function open(p: WoPaper) {
    setBusy(p.id)
    try {
      window.open(await woPaperViewUrl(p.file_path), '_blank', 'noopener')
    } catch { toast.error('That file could not be opened.') }
    finally { setBusy(null) }
  }

  async function save(p: WoPaper) {
    setBusy(p.id)
    try {
      await saveAttachment(WO_PAPER_BUCKET, p.file_path, p.file_name)
    } catch { toast.error('That file could not be downloaded.') }
    finally { setBusy(null) }
  }

  const Row = ({ p, tone }: { p: WoPaper; tone?: 'signed' }) => (
    <div className={
      tone === 'signed'
        ? 'flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm'
        : 'flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm'
    }>
      {tone === 'signed'
        ? <FileCheck2 className="h-4 w-4 shrink-0 text-emerald-600" />
        : <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />}
      <span className="pill-grey shrink-0">{ATTACHMENT_KIND_LABEL[p.kind] ?? p.kind}</span>
      <span className="min-w-0 flex-1 truncate">{p.file_name}</span>
      <span className="hidden shrink-0 text-xs text-slate-400 md:inline">{dateTime(p.uploaded_at)}</span>
      <button className="btn-ghost btn-sm shrink-0" disabled={busy === p.id}
              title="Open in a new tab" onClick={() => void open(p)}>
        <ExternalLink className="h-4 w-4" /> Open
      </button>
      <button className="btn-ghost btn-sm shrink-0" disabled={busy === p.id}
              title="Save to this computer" onClick={() => void save(p)}>
        <Download className="h-4 w-4" /> Download
      </button>
    </div>
  )

  return (
    <Card
      title="Papers on this contract"
      subtitle="The quotation the rates came from, the contractor's registrations and certificates, and the copy he signed."
    >
      {loading ? <Loading label="Reading the file…" /> : (
        <div className="space-y-4">

          {/* ---------- the signed copy ---------- */}
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5" /> The signed and accepted order
            </p>

            {signed.length > 0
              ? <div className="space-y-1">{signed.map((p) => <Row key={p.id} p={p} tone="signed" />)}</div>
              : (
                <p className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500">
                  {acknowledged
                    ? 'The contractor has acknowledged this order. Scan the copy he signed and attach it below — that scan, not the copy OVAL sent, is what the company holds on file.'
                    : 'Nothing to scan yet. This appears once the contractor has acknowledged the order.'}
                </p>
              )}

            {canEdit && acknowledged && (
              <label className="btn-primary btn-sm mt-2 cursor-pointer">
                <Upload className="h-4 w-4" />
                {signed.length ? 'Attach another signed copy' : 'Upload the signed copy'}
                <input type="file" className="hidden" accept="image/*,application/pdf"
                       disabled={busy === 'upload'}
                       onChange={(e) => {
                         const f = e.target.files?.[0]
                         if (f) void put('SIGNED_WO', f)
                         e.target.value = ''
                       }} />
              </label>
            )}
          </div>

          {/* ---------- everything else ---------- */}
          <div className="border-t border-slate-200 pt-4">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              Supporting papers
            </p>
            {others.length > 0
              ? <div className="space-y-1">{others.map((p) => <Row key={p.id} p={p} />)}</div>
              : <p className="text-xs text-slate-500">
                  Nothing on file. The quotation is the one an auditor asks for first.
                </p>}

            {canEdit && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Select className="w-auto min-w-[220px]" value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        options={KINDS.map((k) => ({
                          value: k, label: ATTACHMENT_KIND_LABEL[k] ?? k }))} />
                <label className="btn-secondary cursor-pointer">
                  <Upload className="h-4 w-4" /> Attach
                  <input type="file" className="hidden" accept="image/*,application/pdf"
                         disabled={busy === 'upload'}
                         onChange={(e) => {
                           const f = e.target.files?.[0]
                           if (f) void put(kind, f)
                           e.target.value = ''
                         }} />
                </label>
                {busy === 'upload' && <span className="text-xs text-slate-500">Uploading…</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}
